import { useState } from 'react';
import { X, Edit2, Trash2, Shield, Heart, Zap, Sword, Crown, Users, BookOpen, Settings, Skull, Footprints } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const TABS = [
  { id: 'overview', label: 'Overview', icon: BookOpen },
  { id: 'attributes', label: 'Stats & Traits', icon: Shield },
  { id: 'combat', label: 'Combat', icon: Sword },
];

export default function CharacterDetail({ character, onClose, onEdit, onDelete }) {
  const [activeTab, setActiveTab] = useState('overview');

  const hpPercent = character.stats?.hp?.max 
    ? (character.stats.hp.current / character.stats.hp.max) * 100 
    : 100;
  const hpClass = hpPercent <= 25 ? 'low' : hpPercent <= 50 ? 'medium' : '';

  const getModifier = (score) => {
    if (!score) return '+0';
    const mod = Math.floor((score - 10) / 2);
    return mod >= 0 ? `+${mod}` : `${mod}`;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Character Image */}
            {character.image && (
              <div className="aspect-video rounded-lg overflow-hidden border border-stone-700">
                <img src={character.image} alt={character.name} className="w-full h-full object-cover" />
              </div>
            )}
            
            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="stat-item">
                <div className="stat-value flex items-center justify-center gap-1">
                  <Heart className="w-5 h-5 text-red-500" />
                  {character.stats?.hp?.current || 0}/{character.stats?.hp?.max || 0}
                </div>
                <div className="stat-label">Hit Points</div>
              </div>
              <div className="stat-item">
                <div className="stat-value flex items-center justify-center gap-1">
                  <Shield className="w-5 h-5 text-blue-500" />
                  {character.stats?.ac || 0}
                </div>
                <div className="stat-label">Armor Class</div>
              </div>
              <div className="stat-item">
                <div className="stat-value flex items-center justify-center gap-1">
                  <Footprints className="w-5 h-5 text-green-500" />
                  {character.stats?.speed || 0}
                </div>
                <div className="stat-label">Speed</div>
              </div>
            </div>

            {/* HP Bar */}
            {character.stats?.hp?.max && (
              <div className="hp-bar-container">
                <div className={`hp-bar ${hpClass}`} style={{ width: `${hpPercent}%` }} />
                <div className="hp-text">{character.stats.hp.current} / {character.stats.hp.max}</div>
              </div>
            )}

            {/* Description */}
            {character.description && (
              <div className="p-4 bg-stone-800/50 rounded-lg border border-stone-700">
                <p className="text-stone-300 leading-relaxed">{character.description}</p>
              </div>
            )}
          </div>
        );

      case 'attributes':
        return (
          <div className="space-y-6">
            {/* Ability Scores */}
            <div>
              <h3 className="font-cinzel text-amber-500 text-sm uppercase tracking-wider mb-3">Ability Scores</h3>
              <div className="stat-block">
                {['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'].map(stat => (
                  <TooltipProvider key={stat}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="stat-item">
                          <div className="stat-label">{stat.slice(0, 3)}</div>
                          <div className="stat-value">{character.stats?.[stat] || 10}</div>
                          <div className="stat-modifier">{getModifier(character.stats?.[stat])}</div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="capitalize">{stat}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
              </div>
            </div>

            {/* Traits */}
            <div>
              <h3 className="font-cinzel text-amber-500 text-sm uppercase tracking-wider mb-3">Traits</h3>
              {Object.entries(character.traits || {}).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(character.traits || {}).map(([key, value]) => (
                    <div key={key} className="p-3 bg-stone-800/50 rounded-lg border border-stone-700">
                      <div className="font-cinzel text-amber-500/80 text-xs uppercase tracking-wider mb-1">{key}</div>
                      <div className="text-stone-200">
                        {Array.isArray(value) ? value.join(', ') : String(value)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-stone-500 py-4">No traits defined</div>
              )}
            </div>
          </div>
        );

      case 'combat':
        return (
          <div className="space-y-6">
            {/* Actions */}
            <div>
              <h3 className="font-cinzel text-amber-500 text-sm uppercase tracking-wider mb-3">Actions</h3>
              {(character.actions || []).length > 0 ? (
                <div className="space-y-3">
                  {(character.actions || []).map((action, index) => (
                    <ActionCard key={action.id || index} action={action} />
                  ))}
                </div>
              ) : (
                <div className="text-center text-stone-500 py-4">No actions defined</div>
              )}
            </div>

            {/* Legendary Actions */}
            {(character.legendaryActions || []).length > 0 && (
              <div>
                <h3 className="font-cinzel text-amber-500 text-sm uppercase tracking-wider mb-3">Legendary Actions</h3>
                <div className="space-y-3">
                  {(character.legendaryActions || []).map((action, index) => (
                    <ActionCard key={action.id || index} action={action} isLegendary />
                  ))}
                </div>
              </div>
            )}

            {/* Systems */}
            {(character.systems?.bossBar?.enabled || character.systems?.chaos?.enabled) && (
              <div>
                <h3 className="font-cinzel text-amber-500 text-sm uppercase tracking-wider mb-3">Systems</h3>
                <div className="space-y-3">
                  {character.systems?.bossBar?.enabled && (
                    <div className="p-4 bg-stone-800/50 rounded-lg border border-red-900/50">
                      <div className="font-cinzel text-red-500 text-sm uppercase tracking-wider mb-2 flex items-center gap-2">
                        <Skull className="w-4 h-4" />
                        Boss Bar
                      </div>
                      <p className="text-stone-300 text-sm">
                        Retreat trigger: {character.systems.bossBar.retreatTriggerHP} HP
                      </p>
                    </div>
                  )}

                  {character.systems?.chaos?.enabled && (
                    <div className="p-4 bg-stone-800/50 rounded-lg border border-purple-900/50">
                      <div className="font-cinzel text-purple-500 text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Zap className="w-4 h-4" />
                        Chaos System ({character.systems.chaos.dice})
                      </div>
                      <div className="space-y-2">
                        {(character.systems.chaos.table || []).map((entry, i) => (
                          <div key={i} className="flex gap-3 text-sm">
                            <span className="font-cinzel text-purple-400 w-6">{entry.roll}</span>
                            <span className="text-stone-300">{entry.effect}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Minions */}
            {(character.children || []).length > 0 && (
              <div>
                <h3 className="font-cinzel text-amber-500 text-sm uppercase tracking-wider mb-3">Minions</h3>
                <div className="space-y-3">
                  {(character.children || []).map((child, index) => (
                    <div key={child.id || index} className="p-4 bg-stone-800/50 rounded-lg border border-stone-700">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-cinzel font-bold text-stone-200">{child.name}</h4>
                        <span className="text-xs px-2 py-0.5 rounded bg-stone-700 text-stone-400 capitalize">
                          {child.type}
                        </span>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <Heart className="w-3 h-3 text-red-500" />
                          <span className="text-stone-300">{child.stats?.hp?.current || 0}/{child.stats?.hp?.max || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Shield className="w-3 h-3 text-blue-500" />
                          <span className="text-stone-300">{child.stats?.ac || 0}</span>
                        </div>
                      </div>
                      {child.actions && child.actions.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-stone-700">
                          <div className="text-xs text-stone-500 uppercase tracking-wider mb-2">Actions</div>
                          {child.actions.map((action, i) => (
                            <div key={i} className="text-sm text-stone-400">
                              • {action.name} {action.damage?.dice && `(${action.damage.dice} ${action.damage.type})`}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="character-detail-modal"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-stone-900 border border-stone-700 rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-stone-700 bg-gradient-to-r from-stone-900 to-stone-800">
            <div>
              <h2 className="font-cinzel text-2xl font-bold text-amber-500">{character.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded capitalize bg-stone-800 
                  ${character.type === 'boss' ? 'text-red-500' : 
                    character.type === 'enemy' ? 'text-red-400' : 
                    character.type === 'friendly' ? 'text-green-500' : 
                    'text-amber-500'}`}>
                  {character.type}
                </span>
                {character.alignment && (
                  <span className="text-xs text-stone-500 uppercase">{character.alignment}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onEdit}
                className="border-stone-700 text-stone-300 hover:text-amber-500"
                data-testid="edit-character-detail-btn"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onDelete}
                className="border-stone-700 text-stone-300 hover:text-red-500"
                data-testid="delete-character-detail-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="text-stone-400 hover:text-stone-200"
                data-testid="close-character-detail-btn"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex h-[calc(90vh-80px)]">
            {/* Tabs sidebar */}
            <div className="w-56 border-r border-stone-700 bg-stone-950/50 p-3">
              <ScrollArea className="h-full">
                <div className="space-y-2">
                  {TABS.map(tab => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center gap-3 px-4 py-4 rounded-lg text-base transition-colors ${
                          activeTab === tab.id 
                            ? 'bg-amber-700/30 text-amber-500 border border-amber-700/50' 
                            : 'text-stone-400 hover:text-stone-200 hover:bg-stone-800 border border-transparent'
                        }`}
                        data-testid={`tab-${tab.id}`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="font-cinzel font-semibold">{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>

            {/* Tab content */}
            <div className="flex-1 p-6 overflow-hidden">
              <ScrollArea className="h-full pr-4">
                {renderTabContent()}
              </ScrollArea>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function ActionCard({ action, isLegendary = false }) {
  const categoryColors = {
    attack: 'action-card-attack',
    movement: 'action-card-movement',
    control: 'action-card-control',
    utility: '',
    healing: 'border-green-900',
  };

  return (
    <div className={`action-card ${categoryColors[action.category] || ''} ${isLegendary ? 'border-l-4 border-l-amber-500' : ''}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="font-cinzel font-bold text-stone-100">{action.name}</h4>
          <div className="flex items-center gap-2 text-xs text-stone-500">
            {action.category && <span className="capitalize">{action.category}</span>}
            {action.type && <span className="capitalize">• {action.type}</span>}
            {action.range && <span>• {action.range}</span>}
          </div>
        </div>
        {action.attackBonus !== undefined && action.attackBonus !== 0 && (
          <div className="px-2 py-1 bg-stone-900 rounded text-sm">
            <span className="text-stone-400">+</span>
            <span className="font-cinzel text-amber-500">{action.attackBonus}</span>
          </div>
        )}
      </div>

      {action.damage?.dice && (
        <div className="damage-box mb-3">
          <div className="damage-dice">{action.damage.dice}</div>
          <div className="damage-type">{action.damage.type}</div>
        </div>
      )}

      {action.effects && action.effects.length > 0 && (
        <div className="mb-2 space-y-1">
          {action.effects.map((effect, i) => (
            <div key={i} className="text-xs text-stone-400">
              <span className="text-amber-500">{effect.save} DC {effect.dc}</span>
              {effect.fail && <span> • Fail: {effect.fail}</span>}
              {effect.success && <span> • Success: {effect.success}</span>}
            </div>
          ))}
        </div>
      )}

      {action.description && (
        <p className="text-sm text-stone-400">{action.description}</p>
      )}
    </div>
  );
}
